import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Haptics, ImpactStyle, NotificationType } from "@capacitor/haptics";

import OrdersTable from "./OrdersTable";
import PaginationClassic from "../../components/PaginationClassic";
import OrderModal from "./OrderModal.jsx";
import { useSelector, useDispatch } from "react-redux";
import { useSmallSize } from "../../utils/UseSmallSize.jsx";
import { PrimaryButton } from "../../components/Buttons.jsx";
import { generateDemoOrder, getOrderByID } from "../../api/orders.js";
import { getActiveOrders } from "../../store/orders/action.js";
import { useThemeProvider } from "@/utils/ThemeContext.jsx";
import { addToast } from "../../store/app/action.js";
import { OrderStatusInverseEnum } from "@/utils/Utils.js";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Tutorial from "react-joyride";
import { EVENTS, ACTIONS, STATUS } from "react-joyride";

function Orders() {
  const { isApp } = useThemeProvider();
  const [modalOpen, setModalOpen] = useState(false);
  const [orderID, setOrderID] = useState("");
  const [generatingOrder, setGeneratingOrder] = useState(false);
  const [pulling, setPulling] = useState(false);
  const [pullDistance, setPullDistance] = useState(0);
  const [refreshing, setRefreshing] = useState(false);
  const pullStartYRef = useRef(null);
  const containerRef = useRef(null);
  const thresholdHapticTriggeredRef = useRef(false);
  const dispatch = useDispatch();
  const { activeOrders } = useSelector((state) => state.orders);
  const { isDemoUser } = useSelector((state) => state.app);
  const [tutorialOrder, setTutorialOrder] = useState(null);
  const activeOrderCount = useMemo(() => {
    return (activeOrders || []).length;
  }, [activeOrders])
  const isSmallSize = useSmallSize();
  const location = useLocation();
  const navigate = useNavigate();
  const params = useParams();
  useEffect(() => {
    if (!modalOpen) {
      setOrderID("");
    }
  }, [modalOpen])

  const joyrideHelpers = useRef(null);
  const joyrideHelper = useCallback((node) => {
    if (node) {
      joyrideHelpers.current = node;
      node.reset(true);
      setStepIndex(0);
    }
  }, []);
  const handleJoyrideCallback = (data) => {
    const { action, index, origin, status, type } = data;

    if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
      // Update state to advance the tour
      setStepIndex(index + (action === ACTIONS.PREV ? -1 : 1));
    } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
      // You need to set our running state to false, so we can restart if we click start again.
      setRun(false);
    }
  };
  const [run, setRun] = useState(false);
  const [stepIndex, setStepIndex] = useState(0);
  useEffect(() => {
    if(orderID === "tutorial-order" && stepIndex === 1){
      setStepIndex(2);
    }
  }, [orderID, stepIndex]);
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);
  const steps = useMemo(
    () =>
      [
        {
          content: (
            <div>
              <h2 className="text-lg font-bold">This is your Active Order page</h2>
              <p className="mt-3">
                Here you can see a list of orders that are currently active.
              </p>
              <p className="mt-3">
              They are sorted by the most recent order at the bottom, so you should work on them top down.
              </p>
            </div>
          ),
          placement: "center",
          target: "body",
        },
        {
          target: ".__tutorial-orders-table_tutorial-order",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          hideFooter: true,
          spotlightClicks: true,
          content:
            "This is one of your tutorial orders.  You can see a summary of the order in this table view.  Click on the Order Number or the Eye icon to see the details of this order",
        },
        {
          target: ".__tutorial-order-modal_tab_nav",
          content:
            "Different tabs provide different details about this order.  Let's focus on just the Order Details for now.",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_action_bar",
          content:
            "These are available actions you can take on this order.",
          disableBeacon: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_edit_order_button",
          content:
            "The customer called in and wanted to swap out the lime!  Let's edit the order.",
          disableBeacon: true,
          hideFooter: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_add_product_button",
          content:
            "Customer said they wanted 3 lemons instead of the 2 limes.  Let's add the lemons to the order first.  Click the Add Product button to find prodducts to add.",
          disableBeacon: true,
          hideFooter: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_add_product_modal",
          content:
            "Find the lemons here and add it to the order.",
          disableBeacon: true,
          hideFooter: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_container",
          content: (
            <div>
              <p>Continue editing the order by:</p>
              <ul style={{ listStyleType: 'disc', paddingLeft: '20px', textAlign: 'left' }}>
                <li>
                  Removing the limes
                  {(() => {
                    const limeProduct = tutorialOrder?.products.find(p => p.sku_id === "tutorial-lime");
                    const isRemoved = !limeProduct || limeProduct.quantity === 0;
                    return isRemoved ? (
                      <span style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    ) : (
                      <span style={{ color: '#ef4444', marginLeft: '8px' }}>✗</span>
                    );
                  })()}
                </li>
                <li>
                  Set quantity of lemons to 3
                  {(() => {
                    const lemonProduct = tutorialOrder?.products.find(p => p.sku_id === "tutorial-lemon");
                    const isCorrect = lemonProduct?.quantity === 3;
                    return isCorrect ? (
                      <span style={{ color: '#10b981', marginLeft: '8px' }}>✓</span>
                    ) : (
                      <span style={{ color: '#ef4444', marginLeft: '8px' }}>✗</span>
                    );
                  })()}
                </li>
                <li>Save the Order</li>
              </ul>
            </div>
          ),
          disableBeacon: true,
          hideFooter: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          target: ".__tutorial-order-modal_accept_order_button",
          content: (
            <div>
              For a delivery order, the last thing you have to do is to accept the order.  A delivery courier will come pick up the order and it will automatically progress to the next steps.
            </div>
          ),
          disableBeacon: true,
          hideFooter: true,
          disableOverlayClose: true,
          hideCloseButton: true,
          spotlightClicks: true,
        },
        {
          content: (
            <div>
              <h2>Let's all folks</h2>
              <p>
                I'll continue to guide you through the app as you navigate
                around. Start exploring!
              </p>
            </div>
          ),
          placement: "center",
          target: "body",
        },
      ].filter((step) => Boolean(step)),

    [windowWidth, tutorialOrder]
  );

  useEffect(() => {
    const initFromUrl = async () => {
      const searchParams = new URLSearchParams(location.search);
      const queryId = searchParams.get("id");
      const routeId = params.id;
      const targetId = routeId || queryId;

      if (!targetId) return;

      try {
        const res = await getOrderByID(targetId);
        const order = res?.order;

        if (order && typeof order.order_status === "number") {
          const statusName = OrderStatusInverseEnum[order.order_status];
          const closedStatuses = [
            "COMPLETED",
            "REFUNDED"
          ];

          if (closedStatuses.includes(statusName)) {
            navigate(`/past-orders/${targetId}`, { replace: true });
            return;
          }
        }
      } catch (e) {
        // If lookup fails, fall back to opening in the active orders modal
      }

      // Normalize ?id=xxx to /orders/xxx for active orders
      if (!routeId && queryId) {
        navigate(`/orders/${queryId}`, { replace: true });
        return;
      }

      setTimeout(() => {
        setOrderID(targetId);
        setModalOpen(true);
      }, 250);
    };

    initFromUrl();
  }, [location.search, params.id, navigate]);

  // Pull-to-refresh handlers (app only)
  const onTouchStart = useCallback((e) => {
    if (!isApp || refreshing || modalOpen) return;
    const atTop = (window.scrollY || document.documentElement.scrollTop || 0) === 0;
    if (!atTop) return;
    pullStartYRef.current = e.targetTouches[0].clientY;
    setPulling(true);
    setPullDistance(0);
    thresholdHapticTriggeredRef.current = false;
  }, [isApp, refreshing, modalOpen]);

  const onTouchMove = useCallback(async (e) => {
    if (!isApp || refreshing || modalOpen || !pulling || pullStartYRef.current == null) return;
    const currentY = e.targetTouches[0].clientY;
    const diff = currentY - pullStartYRef.current;
    if (diff > 0) {
      // Dampen pull distance for UX
      const newDistance = Math.min(120, diff * 0.6);
      setPullDistance(newDistance);
      
      // Trigger haptic when crossing threshold
      const threshold = 60;
      if (newDistance >= threshold && !thresholdHapticTriggeredRef.current) {
        thresholdHapticTriggeredRef.current = true;
        try {
          await Haptics.impact({ style: ImpactStyle.Medium });
        } catch (err) {
          // Haptics not available, ignore
        }
      } else if (newDistance < threshold && thresholdHapticTriggeredRef.current) {
        thresholdHapticTriggeredRef.current = false;
      }
    }
  }, [isApp, pulling, refreshing, modalOpen]);

  const onTouchEnd = useCallback(async () => {
    if (!isApp || refreshing || modalOpen) return;
    const threshold = 60;
    const shouldRefresh = pulling && pullDistance >= threshold;
    setPulling(false);
    pullStartYRef.current = null;
    if (shouldRefresh) {
      try {
        // Haptic feedback when refresh starts
        try {
          await Haptics.notification({ type: NotificationType.Success });
        } catch (err) {
          // Haptics not available, ignore
        }
        setRefreshing(true);
        await dispatch(getActiveOrders(true));
      } finally {
        setRefreshing(false);
        setPullDistance(0);
        thresholdHapticTriggeredRef.current = false;
      }
    } else {
      setPullDistance(0);
      thresholdHapticTriggeredRef.current = false;
    }
  }, [dispatch, isApp, pulling, pullDistance, refreshing, modalOpen]);

  return (
    <div
      ref={containerRef}
      className={isSmallSize ? "px-1 py-2" : "px-4 sm:px-6 lg:px-8 py-8 w-full max-w-9xl mx-auto"}
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
    >
      {/*  <Tutorial*/}
      {/*  steps={steps}*/}
      {/*  getHelpers={joyrideHelper}*/}
      {/*  callback={handleJoyrideCallback}*/}
      {/*  locale={{*/}
      {/*    nextLabelWithProgress: "Next",*/}
      {/*  }}*/}
      {/*  run={run}*/}
      {/*  continuous={true}*/}
      {/*  showProgress*/}
      {/*  showSkipButton*/}
      {/*  stepIndex={stepIndex}*/}
      {/*/>*/}
      {isApp && !modalOpen && (
        <div
          className="flex items-center justify-center text-slate-500 dark:text-slate-400 select-none"
          style={{
            height: pulling ? Math.floor(pullDistance) : (refreshing ? 60 : 0),
            transition: (pulling || refreshing) ? "none" : "height 120ms ease-out"
          }}
        >
          {refreshing ? (
            <div className="flex items-center gap-2 text-xs">
              <svg className="animate-spin h-4 w-4 text-slate-500" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z"></path>
              </svg>
              Refreshing...
            </div>
          ) : (
            pulling && pullDistance > 0 && (
              <div className="text-xs">
                {pullDistance >= 60 ? "Release to refresh" : "Pull to refresh"}
              </div>
            )
          )}
        </div>
      )}
      {/* Page header */}
      <div className={isSmallSize ? "sm:flex sm:justify-between sm:items-center mb-4" : "sm:flex sm:justify-between sm:items-center mb-8"}>
        {/* Left: Title */}
        <div className={"mb-4 sm:mb-0"}>
          <h1 className="text-2xl md:text-3xl text-slate-800 dark:text-slate-100 font-bold">
            Active Orders{" "}
            <span className="text-slate-400 dark:text-slate-500 font-medium">{activeOrderCount}</span>
          </h1>
        </div>
        
        {/* Right: Demo Generate Orders Button */}
        {isDemoUser && (
          <div className="mb-4 sm:mb-0">
            <PrimaryButton
              loading={generatingOrder}
              onClick={async () => {
                try {
                  setGeneratingOrder(true);
                  await generateDemoOrder();
                  // Refresh active orders list
                  await dispatch(getActiveOrders(true));
                  dispatch(addToast({
                    type: "success",
                    message: "Sample order generated successfully",
                    timer: 5,
                  }));
                } catch (error) {
                  dispatch(addToast({
                    type: "error",
                    message: error.message || "Failed to generate sample order",
                    timer: 5,
                  }));
                } finally {
                  setGeneratingOrder(false);
                }
              }}
            >
              Generate Sample Orders
            </PrimaryButton>
          </div>
        )}
      </div>

      {/* Table */}
      <OrdersTable
        handleOpenOrder={(orderID) => {
          navigate(`/orders/${orderID}`);
        }}
        openedOrderID={orderID}
        isSmallSize={isSmallSize}
      />
      <OrderModal
        modalOpen={modalOpen}
        setModalOpen={(open) => {
          if (!open) {
            setModalOpen(false);
            setOrderID("");
            if (params.id) {
              navigate("/orders", { replace: true });
            } else if (location.search.includes("id=")) {
              navigate(location.pathname, { replace: true });
            }
          } else {
            setModalOpen(true);
          }
        }}
        orderID={orderID}
        tutorialRunning={run}
        tutorialStepIndex={stepIndex}
        tutorialNextStep={() => {
          setStepIndex(stepIndex + 1);
        }}
        setTutorialOrder={(order) => {
          setTutorialOrder(order);
        }}
      />

      {/* Pagination */}
      <div className="mt-8">
        <PaginationClassic />
      </div>
    </div>
  );
}

export default Orders;
